import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Portal } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { menuContentStyle } from '../constants/Styles';
import type {
  LocationAutocompleteProps,
  PlacePrediction,
} from './LocationAutocomplete.types';
import { TextReg } from './StyledText';
import { TextInput } from './TextInput';

// Google Maps API types
interface GoogleMapsAutocompleteService {
  getPlacePredictions: (
    request: { input: string; location?: unknown; radius?: number },
    callback: (predictions: unknown, status: string) => void,
  ) => void;
}

interface GoogleMapsPlacesService {
  getDetails: (
    request: { placeId: string },
    callback: (place: unknown, status: string) => void,
  ) => void;
}

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google: {
      maps: {
        LatLng: new (lat: number, lng: number) => unknown;
        places: {
          AutocompleteService: new () => GoogleMapsAutocompleteService;
          PlacesService: new (div: HTMLDivElement) => GoogleMapsPlacesService;
          PlacesServiceStatus: {
            OK: string;
          };
        };
      };
    };
  }
}

export function LocationAutocomplete({
  apiKey,
  onLocationSelect,
  placeholder = 'Search for a location...',
}: LocationAutocompleteProps) {
  const [searchText, setSearchText] = useState('');
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const [anchorHeight, setAnchorHeight] = useState(0);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const [hoveredPredictionId, setHoveredPredictionId] = useState<string | null>(
    null,
  );
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const anchorRef = useRef<View | null>(null);
  const autocompleteService = useRef<GoogleMapsAutocompleteService | null>(
    null,
  );
  const placesService = useRef<GoogleMapsPlacesService | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const inputRef = useRef(null);

  const updateAnchorMetrics = useCallback(() => {
    if (!anchorRef.current) {
      return;
    }

    anchorRef.current.measureInWindow((x, y, width, height) => {
      setAnchorWidth(width);
      setAnchorHeight(height);
      setAnchorPosition({ top: y, left: x });
    });
  }, []);

  // Load Google Maps JavaScript API
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if already loaded
    if (window.google && window.google.maps) {
      console.log('Google Maps already loaded');
      setIsScriptLoaded(true);
      return;
    }

    // Load the script
    console.log('Loading Google Maps script...');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Maps script loaded successfully');
      setIsScriptLoaded(true);
    };
    script.onerror = (error) => {
      console.error('Failed to load Google Maps script:', error);
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  // Initialize services when script is loaded
  useEffect(() => {
    if (!isScriptLoaded || !window.google) {
      console.log('Services not ready:', {
        isScriptLoaded,
        hasGoogle: !!window.google,
      });
      return;
    }

    console.log('Initializing Google Maps services...');
    autocompleteService.current =
      new window.google.maps.places.AutocompleteService();
    // Create a div element for PlacesService (required but not displayed)
    const div = document.createElement('div');
    placesService.current = new window.google.maps.places.PlacesService(div);
    console.log('Services initialized successfully');
  }, [isScriptLoaded]);

  // Search places using Google Maps JS API
  const searchPlaces = (query: string) => {
    console.log('searchPlaces called with:', query, {
      hasService: !!autocompleteService.current,
    });

    if (!query.trim() || query.length < 3 || !autocompleteService.current) {
      console.log('Search skipped:', {
        isEmpty: !query.trim(),
        tooShort: query.length < 3,
        noService: !autocompleteService.current,
      });
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    console.log('Starting search for:', query);
    setIsLoading(true);

    // Capture service reference for use in nested function
    const service = autocompleteService.current;

    // Get user's location for biasing
    const getUserLocationAndSearch = () => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = new window.google.maps.LatLng(
              position.coords.latitude,
              position.coords.longitude,
            );

            service.getPlacePredictions(
              {
                input: query,
                location: location,
                radius: 50000, // 50km
              },
              handlePredictions,
            );
          },
          () => {
            // Location permission denied, search without bias
            service.getPlacePredictions({ input: query }, handlePredictions);
          },
        );
      } else {
        // No geolocation support, search without bias
        service.getPlacePredictions({ input: query }, handlePredictions);
      }
    };

    const handlePredictions = (predictionsResult: unknown, status: string) => {
      setIsLoading(false);

      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictionsResult
      ) {
        setPredictions(
          (
            predictionsResult as Array<{
              place_id: string;
              description: string;
            }>
          ).map((p) => ({
            place_id: p.place_id,
            description: p.description,
          })),
        );
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    };

    // Execute the search
    getUserLocationAndSearch();
  };

  // Handle text change with debouncing
  const handleTextChange = (text: string) => {
    setSearchText(text);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set new timeout
    debounceTimeout.current = setTimeout(() => {
      searchPlaces(text);
    }, 300);
  };

  // Fetch place details using Places Service
  const handlePlaceSelect = (placeId: string, description: string) => {
    setShowDropdown(false);
    setSearchText(description);

    if (!placesService.current) return;

    setIsLoading(true);

    placesService.current.getDetails(
      { placeId },
      (place: unknown, status: string) => {
        setIsLoading(false);

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const placeResult = place as {
            name?: string;
            address_components?: Array<{
              long_name: string;
              short_name: string;
              types: string[];
            }>;
            types?: string[];
          };
          const addressComponents = placeResult.address_components || [];

          // Extract address components
          let streetNumber = '';
          let route = '';
          let city = '';
          let state = '';
          let postalCode = '';

          addressComponents.forEach(
            (component: {
              long_name: string;
              short_name: string;
              types: string[];
            }) => {
              const types = component.types;

              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              } else if (types.includes('route')) {
                route = component.long_name;
              } else if (types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name;
              } else if (types.includes('postal_code')) {
                postalCode = component.long_name;
              }
            },
          );

          const address = [streetNumber, route].filter(Boolean).join(' ');

          const rawName = (placeResult.name ?? '').trim();
          const normalizedAddress = (address || '').trim().toLowerCase();
          const normalizedDescription = description.trim().toLowerCase();
          const types: string[] = Array.isArray(placeResult.types)
            ? placeResult.types
            : [];
          const hasPoiType = types.some((type) => placeNameTypes.has(type));

          const normalizedRawName = rawName.toLowerCase();
          const isNumericLeading = /^[0-9]/.test(rawName);
          const isSameAsAddress =
            normalizedRawName.length > 0 &&
            (normalizedRawName === normalizedAddress ||
              normalizedDescription.startsWith(normalizedRawName));
          const resolvedName =
            hasPoiType || (!isNumericLeading && !isSameAsAddress)
              ? rawName
              : '';

          onLocationSelect({
            name: resolvedName,
            address: address || '',
            city: city || '',
            state: state || '',
            postalCode: postalCode || '',
          });
        }
      },
    );
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (showDropdown) {
      requestAnimationFrame(updateAnchorMetrics);
    }
  }, [showDropdown, updateAnchorMetrics]);

  return (
    <View style={styles.container}>
      <View
        ref={anchorRef}
        style={styles.inputContainer}
        onLayout={updateAnchorMetrics}
      >
        <TextInput
          ref={inputRef}
          label=""
          value={searchText}
          onChange={handleTextChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && (
          <View style={styles.loadingIndicator}>
            <ActivityIndicator size="small" color={Colors.purple1} />
          </View>
        )}

        {showDropdown && predictions.length > 0 && (
          <Portal>
            <View pointerEvents="box-none" style={styles.overlay}>
              <Pressable
                accessibilityRole="button"
                style={styles.backdrop}
                onPress={() => {
                  setShowDropdown(false);
                  setHoveredPredictionId(null);
                }}
              />
              <View
                style={[
                  styles.anchoredDropdown,
                  menuContentStyle,
                  {
                    width: anchorWidth,
                    top: anchorPosition.top + anchorHeight,
                    left: anchorPosition.left,
                  },
                ]}
              >
                <ScrollView
                  style={styles.dropdownScroll}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                >
                  {predictions.map((prediction, index) => (
                    <TouchableOpacity
                      key={prediction.place_id}
                      activeOpacity={0.6}
                      style={[
                        styles.menuItem,
                        index === predictions.length - 1
                          ? styles.menuItemLast
                          : undefined,
                        hoveredPredictionId === prediction.place_id
                          ? styles.menuItemHovered
                          : undefined,
                      ]}
                      onPress={() =>
                        handlePlaceSelect(
                          prediction.place_id,
                          prediction.description,
                        )
                      }
                      onPressIn={() =>
                        (
                          inputRef.current as { focus?: () => void } | null
                        )?.focus?.()
                      }
                      // @ts-expect-error - react-native-web specific event to keep input focus
                      onMouseDown={(event: MouseEvent) => {
                        event.preventDefault();
                        (
                          inputRef.current as { focus?: () => void } | null
                        )?.focus?.();
                      }}
                      onMouseEnter={() =>
                        setHoveredPredictionId(prediction.place_id)
                      }
                      onMouseLeave={() =>
                        setHoveredPredictionId((current) =>
                          current === prediction.place_id ? null : current,
                        )
                      }
                    >
                      <TextReg style={styles.menuItemTitle}>
                        {prediction.description}
                      </TextReg>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>
          </Portal>
        )}
      </View>
    </View>
  );
}

const placeNameTypes = new Set([
  'establishment',
  'point_of_interest',
  'food',
  'restaurant',
  'cafe',
  'bar',
  'store',
  'museum',
  'park',
  'church',
  'school',
  'university',
  'gym',
  'lodging',
  'night_club',
  'shopping_mall',
  'stadium',
  'spa',
  'bank',
  'doctor',
  'hospital',
  'pharmacy',
  'police',
  'post_office',
  'library',
  'movie_theater',
  'real_estate_agency',
  'travel_agency',
  'zoo',
]);

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
  },
  loadingIndicator: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -10 }],
  },
  anchoredDropdown: {
    position: 'absolute',
    maxHeight: 260,
    overflow: 'hidden',
    backgroundColor: Colors.purple0,
    borderRadius: 8,
  },
  dropdownScroll: {
    maxHeight: 260,
    backgroundColor: Colors.purple0,
  },
  menuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.opacity10,
    backgroundColor: Colors.purple0,
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
  menuItemHovered: {
    backgroundColor: Colors.purple1opacity50,
  },
  menuItemTitle: {
    fontSize: 14,
    color: Colors.text,
  },
});
